import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import { AlertIcon, CheckIcon } from '@eventbrite/marmalade';
import React, { useEffect } from 'react';
import { useAppContext } from '../../AppContext';
import {
    useLoadedSavedAutoCreateFormValues,
    useLoadedTransientEvent,
    useSavedAutoCreatedEventFormValuesContext,
    useTransientAutoCreatedEventContext,
} from '../../DraftEventStoreProvider';
import {
    Alert,
    AppProps,
    BASE_PATH,
    FormHeader,
    IAuthProps,
    LayoutBackground,
    MOBILE_BREAKPOINT,
    Progress,
    ProgressBar,
} from '../../shared';
import { useEventCreationProcess } from '../Form/hooks';
import { useGetDefaultOrganizerQuery } from '../Form/hooks/useGetDefaultOrganizerQuery';
import { CreateAccountModal } from '../Form/SignUp';
import {
    AboutEvent,
    EventDateAndLocation,
    EventDescription,
    HeaderImage,
    RefundPolicy,
    Tags,
    Tickets,
} from './components';
import { FloatingCard } from './FloatingCard';

export enum PreviewScreenTestId {
    Container = 'PreviewScreenContainer',
}

const EventCreationError = () => {
    const { eventCreationFailed, isModalShowing } = useAppContext();

    return eventCreationFailed && !isModalShowing ? (
        <Alert
            title={gettext("Let's try one more time").toString()}
            description={gettext(
                "We need to try and retrieve your event details again. Don't worry, none of your event info was lost.",
            ).toString()}
            variant="danger"
            icon={<AlertIcon />}
        />
    ) : null;
};

const EventCreationSuccessfulAlert = () => {
    const { showRetrySuccessfulAlert, updateContext } = useAppContext();

    return showRetrySuccessfulAlert ? (
        <Alert
            title={gettext(
                'Nice, your event was saved successfully! 🎉',
            ).toString()}
            description={gettext(
                "Your event was saved as a draft and you can continue editing it. We're glad you're here!",
            ).toString()}
            variant="success"
            icon={<CheckIcon />}
            onClose={() =>
                updateContext({
                    dismissedRetrySuccessfulAlert: true,
                })
            }
        />
    ) : null;
};

export const PreviewScreen = (props: AppProps & IAuthProps) => {
    const { loadedTransientAutoCreatedEvent } = useLoadedTransientEvent();
    const { loadedSavedAutoCreateFormValues } =
        useLoadedSavedAutoCreateFormValues();
    const { transientAutoCreatedEvent } = useTransientAutoCreatedEventContext();
    const { savedAutoCreateFormValues } =
        useSavedAutoCreatedEventFormValuesContext();

    const { isCreatingOrganizationAndOrEvent } = useEventCreationProcess();

    const {
        _activeOrganizationId,
        isLoadingInInnerComponent,
        isFullPageLoading,
        updateContext,
    } = useAppContext();

    const { isLoading: isOrganizerQueryLoading } = useGetDefaultOrganizerQuery(
        _activeOrganizationId,
    );

    useEffect(() => {
        updateContext({
            isFullPageLoading:
                isOrganizerQueryLoading || isCreatingOrganizationAndOrEvent,
        });
    }, [
        isOrganizerQueryLoading,
        isCreatingOrganizationAndOrEvent,
        updateContext,
    ]);

    useEffect(() => {
        if (
            (loadedTransientAutoCreatedEvent && !transientAutoCreatedEvent) ||
            (loadedSavedAutoCreateFormValues && !savedAutoCreateFormValues)
        ) {
            // no transient event or saved auto create form values to create event with, redirect to the first step
            window.location.href = BASE_PATH;
        }
    }, [
        transientAutoCreatedEvent,
        loadedTransientAutoCreatedEvent,
        savedAutoCreateFormValues,
        loadedSavedAutoCreateFormValues,
    ]);

    const hasLoadedSessionData =
        loadedTransientAutoCreatedEvent && loadedSavedAutoCreateFormValues;

    const showSpinner =
        (isFullPageLoading && !isLoadingInInnerComponent) ||
        !hasLoadedSessionData;

    const authProps: IAuthProps = {
        request: props?.request,
        gaSettings: props?.gaSettings,
        recaptcha2PublicKey: props?.recaptcha2PublicKey,
        recaptcha3PublicKey: props?.recaptcha3PublicKey,
        featureFlags: props?.featureFlags,
        socialSSOSettings: props?.socialSSOSettings,
        fp: props?.fp,
    };

    return (
        <>
            <LayoutBackground hideBottomBlob />
            <FormHeaderContainer>
                <FormHeader />
            </FormHeaderContainer>

            <CreateAccountModal authProps={authProps} />

            {showSpinner ? (
                <SpinnerContainer>
                    <Progress />
                </SpinnerContainer>
            ) : (
                <PreviewContainer data-testid={PreviewScreenTestId.Container}>
                    {hasLoadedSessionData && (
                        <ProgressBar
                            step={3}
                            totalSteps={3}
                            text={gettext(
                                'Step 3 of 3: Preview your event',
                            ).toString()}
                        />
                    )}
                    <EventCreationError />
                    <EventCreationSuccessfulAlert />
                    <HeaderImage />
                    <EventDescription />
                    <EventDateAndLocation />
                    <RefundPolicy />
                    <AboutEvent />
                    <Tickets />
                    <Tags />
                </PreviewContainer>
            )}
            {hasLoadedSessionData && <FloatingCard />}
        </>
    );
};

const FormHeaderContainer = styled.div`
    padding: 24px;
`;

const PreviewContainer = styled.div`
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 48px;
    width: 60%;

    padding-bottom: 4.25rem;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
        gap: 32px;
        width: 90%;
    }
`;

const SpinnerContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100vh;
    font-size: 16px;
`;
